"use client"
import {
    Text,
    Image,
    List,
    Title,
    UnstyledButton,
    Box, Grid, AspectRatio, Flex, Button, Modal, Center,
} from '@mantine/core';
import {IconCircleFilled} from '@tabler/icons-react';
import {useEffect, useState} from "react";
import {Attachment, AttachmentPath} from "src/entities/attachments";
import {getProduct, Products} from "src/entities/products";
import {CategoriesWidget, PartnersWidget, ProductsWidget} from "src/widgets";
import {Carousel} from "@mantine/carousel";
import {useDisclosure} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {Content} from "src/shared/helpers/content";
import {AppModal} from "src/shared/ui/modal";
import {COLORS} from "src/constants";
import {AppealFeature} from "src/features/client";
import DocViewer, {DocViewerRenderers} from "@cyntler/react-doc-viewer";
import {Helmet} from "react-helmet";
import {generateMetaData} from "src/shared/config/seo/meta-data";

export const ProductDetails = () => {
    const {productId} = useParams();
    const {t,i18n} = useTranslation()
    const locale = i18n.language
    const [product, setProduct] = useState<Products | undefined>()
    const [openedBiggerView, { toggle: toggleBiggerView }]
        = useDisclosure()
    const [activeFileView, setActiveFileView] = useState<number>(0)
    const [opened, {toggle,close}] = useDisclosure()

    const fetchProductDetails = async () => {
        const res = await getProduct(productId as string,false);
        setProduct(res)
    }
    const filesUrls = [
      ...(product?.photoAttachments?.map(el => ({type: "image", url: AttachmentPath.GetAttachmentUrl(el.id)})) || []),
      ...(product?.videoAttachments?.map(el => ({type: "video", url: AttachmentPath.GetAttachmentUrl(el.id)})) || []),
    ]

    const getSmallFileView = (file: {type: string, url: string}, index: number) => {
        if(file){
            if(file.type === "image") {
                return (
                    <img
                        style={{ cursor: "pointer", objectFit: "contain", maxHeight: "300px" }}
                        onClick={() => {
                            toggleBiggerView()
                            setActiveFileView(index)
                        }}
                        width="50%"
                        height="50%"
                        src={file.url} alt="" />
                )
            }else if(file.type === "video") {
                return (
                    <video controls
                           style={{cursor: "pointer"}}
                           onClick={() => {
                               setActiveFileView(index)
                           }}
                           width="100%"
                           height="100%"
                           src={file.url}/>
                )
            }
        }
    }

    const getModalFileView = () => {
        const file = filesUrls[activeFileView]
        if(file){
            if(file?.type === "image") {
                return (
                    <img
                        style={{ cursor: "pointer", objectFit: "contain" }}
                        width="50%"
                        height="50%"
                        src={file.url} alt="" />
                )
            }else if(file.type === "video") {
                return (
                    <video
                        style={{cursor: "pointer"}}
                        width="100%"
                        height="100%"
                        src={file.url}/>
                )
            }
        }
    }

    useEffect(() => {
        fetchProductDetails()
    }, [productId]);

    const metaData = generateMetaData(
        product?.titleRu || "",
        product?.contentRu || "",
        `category/${product?.category?.id || 1}/product/${product?.id || 1}`
    );

    return (
        <Box mt={{base: 0, sm: 50}}>
            <Helmet>
                <title>{metaData.title}</title>
                {metaData.description && <meta name="description" content={metaData.description}/>}
                {metaData.openGraph && (
                    <meta property="og:title" content={metaData.openGraph.title}/>
                )}
                {metaData.openGraph && (
                    <meta property="og:description" content={metaData.openGraph.description}/>
                )}
                {metaData.openGraph && (
                    <meta property="og:image" content={metaData.openGraph.images}/>
                )}
                {metaData.openGraph && (
                    <meta property="og:url" content={metaData.openGraph.url}/>
                )}
                {metaData.twitter && (
                    <meta name="twitter:card" content={metaData.twitter.card}/>
                )}
                {metaData.twitter && (
                    <meta name="twitter:title" content={metaData.twitter.title}/>
                )}
                {metaData.twitter && (
                    <meta name="twitter:description" content={metaData.twitter.description}/>
                )}
                {metaData.twitter && (
                    <meta name="twitter:image" content={metaData.twitter.images}/>
                )}
            </Helmet>
            <Grid className="container" gutter={{base: 24, sm: 40}} mb={{base: 40, sm: 80}}>
                <Grid.Col bg="rgba(255,255,255,0.4)" span={{base: 12,sm:7, md: 6}}>
                    {filesUrls && filesUrls?.length > 1 ?
                        <Carousel
                            loop
                            withIndicators styles={{
                            indicator: {
                                background: "#2335B5",
                                height: "15px",
                                width: "15px",
                            },
                            control: {
                                background: "#2335B5",
                                color: "white",
                            }
                        }}>
                            {filesUrls?.map((el, index) => (
                                <Carousel.Slide h="100%" key={el.url}>
                                    <AspectRatio h="100%" ratio={1.5}>
                                       <Center>
                                           {getSmallFileView(el, index)}
                                       </Center>
                                    </AspectRatio>
                                </Carousel.Slide>
                            ))}
                    </Carousel> :
                        <AspectRatio h="100%" ratio={1.5}>
                            <Center>
                                {getSmallFileView(filesUrls?.[0], 0)}
                            </Center>
                        </AspectRatio>}
                </Grid.Col>

                <Grid.Col span={{base: 12, sm: 5, md: 6}}>
                    <Box mb={{base: 16, lg: 48}}>
                        <Flex align="center" justify="space-between" wrap="wrap" gap={{base: 10 ,sm: 24}}>
                            <Title lh={1.2} fz={{base: 18,xs: 20, sm: 28}}>
                                {Content.GetTitleByLanguage(product,locale)}
                            </Title>
                            <UnstyledButton w={140} onClick={toggle}
                                bg={COLORS.PRIMARY_BG} style={{ borderRadius: "100px"}}
                                p={{base: 10 ,xs: 14}} fz={14} c={COLORS.PRIMARY_COLOR}>
                                {t("button.appeal")}
                            </UnstyledButton>
                        </Flex>
                    </Box>
                    <List
                        spacing="sm"
                        icon={<IconCircleFilled color="#2335B5" size={10} />}>
                        {product?.characteristics?.map((item) => (
                            <List.Item key={item.id}>
                                <Text w={500}>
                                    {Content.GetTitleByLanguage(item.label,  locale)}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {Content.HtmlRenderFromLocale({
                                        titleKg: item.valueKg,
                                        titleRu: item.valueRu,
                                    }, locale)}
                                </Text>
                            </List.Item>
                        ))}
                    </List>
                    <Text fz={16} lh={1} c="#5C5D61">
                        {Content.HtmlRenderFromLocale({
                            titleKg: product?.contentKg,
                            titleRu: product?.contentRu,
                        },locale)}
                    </Text>
                    {product?.fileAttachments?.length ?
                        <Box>
                            <Title fz={{base: 18, sm: 24}}>
                                {t("section.titles.documents")}
                            </Title>
                            <List>
                                {product?.fileAttachments?.map((item) => <FileItem key={item.id} data={item}/>)}
                            </List>
                    </Box> : null}
                </Grid.Col>
            </Grid>
            <Box className="container">
                <Box mb={{base: 16, sm: 40}}>
                    <Title mb={24} lh={1.2} c="#0C0D13" fz={{base: 32, sm: 48}}>
                        {t("section.titles.similar-products")}
                    </Title>
                    <Text c="#5C5D61" fz={16}>
                        {Content.HtmlRenderFromLocale({
                            titleKg: product?.category?.contentKg,
                            titleRu: product?.category?.contentRu,
                        }, locale)}
                    </Text>
                </Box>
                <Grid w="100%">
                    <ProductsWidget
                        page="productDetail"
                        categoryIdLikeProp={product?.category?.id} />
                </Grid>
                <PartnersWidget/>
            </Box>
            <CategoriesWidget parentId={product?.category?.parentId?.id || product?.category?.id}/>
            <AppModal  size="60vw" opened={openedBiggerView} toggle={toggleBiggerView} closeOnClickOutside>
                <AspectRatio ratio={1.5}>
                   <Center>
                       {getModalFileView()}
                   </Center>
                </AspectRatio>
            </AppModal>
            <Modal centered withCloseButton={false} onClose={close}
                opened={opened}
                   styles={{
                       content: {
                           background: "none",
                           boxShadow: "none",
                       },
                       overlay: {
                           background: "none"
                       }
                    }
                }
                >
                <AppealFeature close={close} productId={product?.id} />
            </Modal>
        </Box>
    );
}


const FileItem = ({data}: {data: Attachment}) => {
    const [opened, {toggle, close}] = useDisclosure()
    const {t} = useTranslation()
    return (
        <Box style={{cursor: "pointer",}} py={14}>
            <List.Item onClick={toggle}>
                {data.name} <Text c="blue">{t("button.open")}</Text>
            </List.Item>
            <Modal size="xl" opened={opened} onClose={close}>
                <DocViewer
                    documents={[{uri: AttachmentPath.GetAttachmentUrl(data.id), fileType: data?.type, fileName: data?.name}]}
                    pluginRenderers={DocViewerRenderers}
                />
            </Modal>
        </Box>
    )
}